// src/components/settings/email-template-preview.jsx
import React, { useMemo } from "react";
import {
  composeSchedulingSellerEmail, composeSchedulingAgentEmail,
  composeUpdateSellerEmail, composeUpdateAgentEmail,
  composeCancellationSellerEmail, composeCancellationAgentEmail,
  composeRecapSellerEmail,
} from "../../utils/openHouseEmailComposer";
import {
  buildBanner, buildSignature, buildFooter, wrapEmail, paragraphsToHtml,
} from "../../utils/openHouseEmailsHtml";
import { DEFAULT_TEMPLATES } from "../../utils/openHouseEmails";
import { DEFAULT_ACCEPTANCE_TEMPLATES } from "../../utils/acceptanceEmails";
import { DEFAULT_TRANSACTION_TEMPLATES } from "../../utils/transactionEmailSequence";
import { interpolate } from "../../utils/emailPlaceholders";

const PREVIEW_OH = {
  id: "preview",
  propertyAddress: "4521 Lakeview Dr, Indianapolis IN 46220",
  sellerName: "Alex Martinez",
  sellerEmail: "alex@example.com",
  date: "2026-05-23",
  startTime: "13:00",
  endTime: "15:00",
  hostingAgentName: "Riley Park",
  hostingAgentEmail: "riley@example.com",
  notes: "Signs going up Friday.",
  lockboxType: "Push Button",
  lockboxNumber: "27",
  lockboxCode: "4521",
  lockbox: "Push Button (code 4521)",
  feedbackToken: "preview-token",
};
const PREVIEW_FB = {
  agentName: "Riley Park", date: "Sat, May 23, 2026", time: "1:00pm – 3:00pm",
  groups: 12, secondShowings: 4,
  agentFeedback: "Loved the natural light and the open kitchen.",
  buyerFeedback: "Multiple groups asked about the school district.",
  otherComments: "Lockbox code worked smoothly.",
};
const PREVIEW_OLD = { date: "2026-05-19", startTime: "13:00", endTime: "15:00" };
const PREVIEW_NEW = { date: "2026-05-23", startTime: "14:00", endTime: "16:00" };
const PREVIEW_ORIGIN = "https://teremn.com";
const PREVIEW_REASON = "Weather forecast looks bad.";

// Sample data used by the generic preview for the non-OH templates.
const PREVIEW_CONTACT = {
  contactFirstName: "Jamie",
  contactName: "Jamie Rivera",
  propertyAddress: "4521 Lakeview Dr, Indianapolis IN 46220",
  yearsInHome: 3,
};

// Sample data for vendor-notification templates (staging / photographer / inspector).
const PREVIEW_VENDOR = {
  vendorName: "Register Redesign",
  propertyAddress: "4521 Lakeview Dr, Indianapolis IN 46220",
  clients: "Brian & Andrea Sattler",
  closingDate: "May 16, 2026",
  targetListDate: "May 7, 2026",
  installDate: "May 7, 2026",
  destageDate: "May 16, 2026",
  photoshootDate: "May 8, 2026",
  inspectionList: "• Full Inspection\n• Radon\n• Sewer Scope",
  lockboxType: "Push Button",
  lockboxNumber: "27",
  lockboxCode: "4521",
  lockbox: "Push Button #27 (code 4521)",
};

// Sample data for transaction-sequence templates (offer accepted, financing,
// appraisal, closing) — covers the {{tokens}} those emails use.
const PREVIEW_TRANSACTION = {
  propertyAddress: "4521 Lakeview Dr, Indianapolis IN 46220",
  clientNames: "Brian & Andrea Sattler",
  sellerNames: "Brian & Andrea Sattler",
  buyerNames: "Jamie Rivera",
};

function renderGenericPreview(templateId, sender, overrides) {
  // Custom (user-created) templates have no hardcoded default — they render
  // straight from the live-edited overrides.
  const def =
    DEFAULT_TEMPLATES[templateId] ||
    DEFAULT_ACCEPTANCE_TEMPLATES[templateId] ||
    DEFAULT_TRANSACTION_TEMPLATES[templateId] ||
    (overrides && overrides.body != null ? { subject: "", body: "" } : null);
  if (!def) return "<p>Start typing your template to see the preview.</p>";
  const isVendor = templateId.endsWith("-vendor");
  // A "pure" transaction template — not also an Open-House/Acceptance id.
  const isTransaction =
    !!DEFAULT_TRANSACTION_TEMPLATES[templateId] &&
    !DEFAULT_TEMPLATES[templateId] &&
    !DEFAULT_ACCEPTANCE_TEMPLATES[templateId];
  const senderName = `${sender?.firstname || "Henry"} ${sender?.lastname || "Edelstein"}`.trim();
  const data = {
    ...(isVendor
      ? PREVIEW_VENDOR
      : isTransaction
        ? { ...PREVIEW_TRANSACTION, agentName: senderName }
        : PREVIEW_CONTACT),
    senderFirstName: sender?.firstname || "Henry",
    senderName,
  };
  const banner = buildBanner({ propertyAddress: data.propertyAddress }, "");
  const bodyText = interpolate(overrides?.body ?? def.body, data);
  const body = paragraphsToHtml(bodyText);
  const signature = buildSignature(sender);
  const footer = buildFooter({});
  return wrapEmail({ banner, body, signature, footer });
}

export default function EmailTemplatePreview({ templateId, sender, overrides }) {
  const html = useMemo(() => {
    const s = sender || { firstname: "Henry", lastname: "Edelstein", email: "henry@teamedelstein.com" };
    try {
      switch (templateId) {
        case "scheduling-seller":   return composeSchedulingSellerEmail(PREVIEW_OH, s, overrides).html;
        case "scheduling-agent":    return composeSchedulingAgentEmail(PREVIEW_OH, s, PREVIEW_ORIGIN, overrides).html;
        case "update-seller":       return composeUpdateSellerEmail(PREVIEW_OH, PREVIEW_OLD, PREVIEW_NEW, s, overrides).html;
        case "update-agent":        return composeUpdateAgentEmail(PREVIEW_OH, PREVIEW_OLD, PREVIEW_NEW, s, PREVIEW_ORIGIN, overrides).html;
        case "cancellation-seller": return composeCancellationSellerEmail(PREVIEW_OH, PREVIEW_REASON, s, overrides).html;
        case "cancellation-agent":  return composeCancellationAgentEmail(PREVIEW_OH, PREVIEW_REASON, s, overrides).html;
        case "recap-seller":        return composeRecapSellerEmail(PREVIEW_OH, PREVIEW_FB, s, overrides).html;
        // Non-OH templates use a shared generic preview.
        case "birthday":
        case "home-anniversary":
        case "closing-day":
        case "welcome":
        case "just-listed":
        case "just-sold":
        case "staging-consult-vendor":
        case "staging-install-vendor":
        case "staging-destage-vendor":
        case "photographer-vendor":
        case "pre-inspection-vendor":
        case "seller-client-accepted":
        case "buyer-client-accepted":
        case "title-new-transaction":
          return renderGenericPreview(templateId, s, overrides);
        default: return renderGenericPreview(templateId, s, overrides);
      }
    } catch (err) {
      return `<pre style="color:#b91c1c;padding:12px;font-family:monospace;font-size:11px;">Preview error: ${err && err.message ? err.message : err}</pre>`;
    }
  }, [templateId, sender, overrides]);

  return (
    <div className="p-3 bg-slate-100">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 px-1">
        Live preview · how the recipient will see this
      </div>
      <iframe
        title="Email preview"
        srcDoc={html}
        className="w-full h-[640px] border border-slate-300 rounded bg-white"
      />
    </div>
  );
}
